import React, { useState, useEffect } from "react";
import { useAccountingReports } from "../../../contextjs/AccountingReportsContext";
import { Alert, Card, Table, Button, Form, Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { apiInstance } from "../../../api/api";
import { AccountingScopeSelect, useAccountingScope } from "../accountingscope"

const IncomeStatementHistoricalReport = () => {
    const { fyId } = useParams();
    const navigate = useNavigate();
    const {
        incomeStatementData,
        incomeStatementLoading,
        incomeStatementError,
        fetchIncomeStatement
    } = useAccountingReports();
    const scope = useAccountingScope();

    const [financialYear, setFinancialYear] = useState(null);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [loading, setLoading] = useState(true);

    // load FY metadata
    useEffect(() => {
        apiInstance
            .get(`/financial-years/${fyId}/`)
            .then(res => {
                setFinancialYear(res.data);
                setStartDate(res.data.start_date);
                setEndDate(res.data.end_date);
            })
            .finally(() => setLoading(false));
    }, [fyId]);

    // fetch report
    useEffect(() => {
        if (!financialYear || scope.loadingScope || !scope.storeId) return;

        fetchIncomeStatement(
            startDate,
            endDate,
            financialYear.id,
            scope.storeId
        );
    }, [financialYear, startDate, endDate, fetchIncomeStatement, scope.loadingScope, scope.storeId]);

    if (scope.scopeError) {
        return <Alert variant="danger">{scope.scopeError}</Alert>;
    }

    if (incomeStatementError) {
        return <Alert variant="danger">{incomeStatementError}</Alert>;
    }

    if (loading || scope.loadingScope || incomeStatementLoading || !incomeStatementData) {
        return <p>Loading...</p>;
    }

    const { income, contra_revenue, net_revenue, expense, net_profit } = incomeStatementData;

    return (
        <div>
            <Button variant="secondary" onClick={() => navigate(-1)}>Back</Button>

            <Card className="mt-4">
                <Card.Header className="text-center">
                    <h2>Income Statement</h2>
                    <p>
                        Financial Year: {financialYear.name}
                        <br />
                        {financialYear.start_date} - {financialYear.end_date}
                    </p>
                    {incomeStatementData?.scope && (
                        <p className="mb-0">{incomeStatementData.scope.effective_store_name}</p>
                    )}
                </Card.Header>

                <Card.Body>
                    <Form className="mb-3">
                        <Row className="align-items-end">
                            <Col md={4}>
                                <Form.Label>Start Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    min={financialYear.start_date}
                                    max={financialYear.end_date}
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </Col>
                            {scope.isAdmin && (
                                <Col md={4}>
                                    <AccountingScopeSelect scope={scope} />
                                </Col>
                            )}
                            <Col md={4}>
                                <Form.Label>End Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    min={financialYear.start_date}
                                    max={financialYear.end_date}
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </Col>
                        </Row>
                    </Form>

                    <Table bordered hover>
                        <tbody>
                        <tr><td colSpan="2"><strong>Income</strong></td></tr>
                        {income.accounts.map((a, i) => (
                            <tr key={i}>
                                <td>{a.account_name}</td>
                                <td className="text-end">{Number(a.balance).toLocaleString()}</td>
                            </tr>
                        ))}
                        <tr>
                            <td><strong>Total Income</strong></td>
                            <td className="text-end"><strong>{Number(income.total).toLocaleString()}</strong></td>
                        </tr>

                        <tr><td colSpan="2"><strong>Contra Revenue</strong></td></tr>
                        {contra_revenue.accounts.map((a, i) => (
                            <tr key={i}>
                                <td>{a.account_name}</td>
                                <td className="text-end">{Number(a.balance).toLocaleString()}</td>
                            </tr>
                        ))}
                        <tr>
                            <td><strong>Total Contra Revenue</strong></td>
                            <td className="text-end"><strong>{Number(contra_revenue.total).toLocaleString()}</strong></td>
                        </tr>

                        <tr>
                            <td><strong>Net Revenue</strong></td>
                            <td className="text-end"><strong>{Number(net_revenue).toLocaleString()}</strong></td>
                        </tr>

                        <tr><td colSpan="2"><strong>Expenses</strong></td></tr>
                        {expense.accounts.map((a, i) => (
                            <tr key={i}>
                                <td>{a.account_name}</td>
                                <td className="text-end">{Number(a.balance).toLocaleString()}</td>
                            </tr>
                        ))}
                        <tr>
                            <td><strong>Total Expenses</strong></td>
                            <td className="text-end"><strong>{Number(expense.total).toLocaleString()}</strong></td>
                        </tr>

                        <tr>
                            <td><strong>Net Profit</strong></td>
                            <td className="text-end"><strong>{Number(net_profit).toLocaleString()}</strong></td>
                        </tr>
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </div>
    );
};

export default IncomeStatementHistoricalReport;